import { Injectable } from '@angular/core';
import { CognitoIdentityClient, GetCredentialsForIdentityCommand, GetIdCommand } from '@aws-sdk/client-cognito-identity';
import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { environment } from '../../../environments/environment';
import { AuthProviderPort, AwsCredentials, SessionUser } from './auth-provider.port';

type StoredSession = {
  username: string;
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
};

type AccessTokenPayload = {
  'username'?: string;
  'cognito:groups'?: string[];
};

type IdTokenPayload = {
  'email'?: string;
  'cognito:username'?: string;
};

type CognitoError = {
  name?: string;
  code?: string;
  message?: string;
};

const REFRESH_SKEW_SECONDS = 30;

@Injectable({
  providedIn: 'root',
})
export class CognitoAuthProviderService implements AuthProviderPort {
  private refreshInFlight?: Promise<StoredSession>;
  private sessionCache?: StoredSession | null;
  private cognitoIdentityClient = new CognitoIdentityClient({
    region: environment.aws.cognitoRegion,
  });

  private userPool = new CognitoUserPool({
    UserPoolId: environment.aws.userPoolId,
    ClientId: environment.aws.userPoolWebClientId,
  });

  async login(username: string, password: string): Promise<void> {
    const user = this.createUser(username);
    const details = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    try {
      const session = await new Promise<CognitoUserSession>((resolve, reject) => {
        user.authenticateUser(details, {
          onSuccess: resolve,
          onFailure: reject,
        });
      });

      this.persistSession(username, session);
    } catch (error) {
      throw this.normalizeCognitoError(error);
    }
  }

  async logout(): Promise<void> {
    const session = this.sessionCache ?? await this.readSessionFromUserPool();

    if (session?.username) {
      try {
        const user = this.createUser(session.username);
        await new Promise<void>((resolve, reject) => {
          user.globalSignOut({
            onSuccess: () => resolve(),
            onFailure: reject,
          });
        });
      } catch {
        // best-effort global sign-out; local cleanup still happens below
      }
    }

    this.clearSession();
  }

  async getAccessToken(): Promise<string> {
    const session = await this.getValidSession();
    return session.accessToken;
  }

  async getSessionUser(): Promise<SessionUser | null> {
    try {
      const session = await this.getValidSession();
      const accessTokenPayload = this.parseJwtPayload<AccessTokenPayload>(session.accessToken);
      const idTokenPayload = this.parseJwtPayload<IdTokenPayload>(session.idToken);

      const username = accessTokenPayload.username || idTokenPayload['cognito:username'] || idTokenPayload.email;

      if (!username) {
        return null;
      }

      return {
        username,
        roles: accessTokenPayload['cognito:groups'] ?? [],
      };
    } catch {
      return null;
    }
  }

  async getAWSCredentials(): Promise<AwsCredentials> {
    const session = await this.getValidSession();
    const providerName = `cognito-idp.${environment.aws.cognitoRegion}.amazonaws.com/${environment.aws.userPoolId}`;

    const getIdResponse = await this.cognitoIdentityClient.send(new GetIdCommand({
      IdentityPoolId: environment.aws.identityPoolId,
      Logins: {
        [providerName]: session.idToken,
      },
    }));

    if (!getIdResponse.IdentityId) {
      throw new Error('auth.errors.sessionExpired');
    }

    const getCredentialsResponse = await this.cognitoIdentityClient.send(new GetCredentialsForIdentityCommand({
      IdentityId: getIdResponse.IdentityId,
      Logins: {
        [providerName]: session.idToken,
      },
    }));

    const credentials = getCredentialsResponse.Credentials;
    if (!credentials?.AccessKeyId || !credentials.SecretKey) {
      throw new Error('auth.errors.sessionExpired');
    }

    return {
      accessKeyId: credentials.AccessKeyId,
      secretAccessKey: credentials.SecretKey,
      sessionToken: credentials.SessionToken,
      expiration: credentials.Expiration,
    };
  }

  async isLoggedIn(): Promise<boolean> {
    try {
      await this.getValidSession();
      return true;
    } catch {
      return false;
    }
  }

  async requestPasswordReset(username: string): Promise<void> {
    try {
      const user = this.createUser(username);
      await new Promise<void>((resolve, reject) => {
        user.forgotPassword({
          onSuccess: () => resolve(),
          onFailure: reject,
        });
      });
    } catch (error) {
      const cognitoError = (error as CognitoError) ?? {};
      const name = cognitoError.name ?? cognitoError.code;

      // Avoid account enumeration in forgot-password flow.
      if (name === 'UserNotFoundException') {
        return;
      }

      throw this.normalizeCognitoError(error);
    }
  }

  async confirmPasswordReset(username: string, code: string, newPassword: string): Promise<void> {
    try {
      const user = this.createUser(username);
      await new Promise<void>((resolve, reject) => {
        user.confirmPassword(code, newPassword, {
          onSuccess: () => resolve(),
          onFailure: reject,
        });
      });
    } catch (error) {
      throw this.normalizeCognitoError(error);
    }
  }

  private async getValidSession(): Promise<StoredSession> {
    const cachedSession = this.sessionCache;
    if (cachedSession && !this.isExpired(cachedSession.expiresAt)) {
      return cachedSession;
    }

    const storedSession = await this.readSessionFromUserPool();
    if (!storedSession) {
      this.clearSession();
      throw new Error('auth.errors.sessionExpired');
    }

    if (!this.isExpired(storedSession.expiresAt)) {
      this.sessionCache = storedSession;
      return storedSession;
    }

    return this.refreshSessionSingleFlight(storedSession.username, storedSession.refreshToken);
  }

  private refreshSessionSingleFlight(username: string, refreshToken: string): Promise<StoredSession> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshSession(username, refreshToken)
        .finally(() => {
          this.refreshInFlight = undefined;
        });
    }

    return this.refreshInFlight;
  }

  private async refreshSession(username: string, refreshToken: string): Promise<StoredSession> {
    if (!username || !refreshToken) {
      this.clearSession();
      throw new Error('auth.errors.sessionExpired');
    }

    const user = this.createUser(username);
    const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

    let nextCognitoSession;
    try {
      nextCognitoSession = await new Promise<CognitoUserSession>((resolve, reject) => {
        user.refreshSession(token, (err, refreshedSession) => {
          if (err || !refreshedSession) {
            reject(err ?? new Error('auth.errors.sessionExpired'));
            return;
          }

          resolve(refreshedSession);
        });
      });
    } catch (error) {
      this.clearSession();
      throw this.normalizeCognitoError(error, 'auth.errors.sessionExpired');
    }

    const nextSession = this.sessionFromCognitoSession(username, nextCognitoSession, refreshToken);
    this.writeSession(nextSession);
    return nextSession;
  }

  private persistSession(username: string, session: CognitoUserSession): void {
    const refreshToken = session.getRefreshToken()?.getToken();

    if (!refreshToken) {
      throw new Error('auth.errors.sessionExpired');
    }

    this.writeSession(this.sessionFromCognitoSession(username, session, refreshToken));
  }

  private sessionFromCognitoSession(username: string, session: CognitoUserSession, refreshToken: string): StoredSession {
    const accessToken = session.getAccessToken().getJwtToken();
    const idToken = session.getIdToken().getJwtToken();
    const expiresAt = session.getAccessToken().getExpiration() * 1000;

    return {
      username,
      accessToken,
      idToken,
      refreshToken,
      expiresAt,
    };
  }

  private parseJwtPayload<T>(token: string): T {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) {
      throw new Error('Invalid token payload');
    }

    const base64 = encodedPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(paddedBase64);
    return JSON.parse(json) as T;
  }

  private calculateExpiry(expiresInSeconds?: number): number {
    const expiresIn = expiresInSeconds ?? 0;
    return Date.now() + (expiresIn * 1000);
  }

  private isExpired(expiresAt: number): boolean {
    return Date.now() >= (expiresAt - (REFRESH_SKEW_SECONDS * 1000));
  }

  private clearSession(): void {
    this.userPool.getCurrentUser()?.signOut();
    this.sessionCache = null;
  }

  private writeSession(session: StoredSession): void {
    this.sessionCache = session;
  }

  private async readSessionFromUserPool(): Promise<StoredSession | null> {
    const currentUser = this.userPool.getCurrentUser();
    if (!currentUser) {
      return null;
    }

    const session = await new Promise<CognitoUserSession>((resolve, reject) => {
      currentUser.getSession((err, currentSession) => {
        if (err || !currentSession) {
          reject(err ?? new Error('auth.errors.sessionExpired'));
          return;
        }

        resolve(currentSession);
      });
    }).catch(() => null);

    if (!session) {
      return null;
    }

    const accessTokenPayload = this.parseJwtPayload<AccessTokenPayload>(session.getAccessToken().getJwtToken());
    const idTokenPayload = this.parseJwtPayload<IdTokenPayload>(session.getIdToken().getJwtToken());
    const username = accessTokenPayload.username || idTokenPayload['cognito:username'] || idTokenPayload.email;
    const refreshToken = session.getRefreshToken()?.getToken();

    if (!username || !refreshToken) {
      return null;
    }

    return this.sessionFromCognitoSession(username, session, refreshToken);
  }

  private normalizeCognitoError(error: unknown, fallback = 'auth.errors.generic'): Error {
    const cognitoError = (error as CognitoError) ?? {};
    const name = cognitoError.name ?? cognitoError.code;
    const message = cognitoError.message?.toLowerCase() ?? '';

    if (
      name === 'PasswordHistoryPolicyViolationException' ||
      message.includes('password history') ||
      message.includes('cannot be reused') ||
      message.includes('can not be reused') ||
      message.includes('reuse')
    ) {
      return new Error('auth.errors.passwordReuse');
    }

    switch (name) {
      case 'NotAuthorizedException':
        return new Error('auth.errors.invalidCredentials');
      case 'UserNotFoundException':
        return new Error('auth.errors.userNotFound');
      case 'CodeMismatchException':
        return new Error('auth.errors.codeMismatch');
      case 'ExpiredCodeException':
        return new Error('auth.errors.expiredCode');
      case 'TooManyRequestsException':
        return new Error('auth.errors.tooManyRequests');
      case 'LimitExceededException':
        return new Error('auth.errors.tooManyRequests');
      case 'PasswordResetRequiredException':
        return new Error('auth.errors.sessionExpired');
      case 'InvalidPasswordException':
        return new Error('auth.errors.invalidPassword');
      default:
        return new Error(fallback);
    }
  }

  private createUser(username: string): CognitoUser {
    return new CognitoUser({
      Username: username,
      Pool: this.userPool,
    });
  }
}
